/**
 * API service for Breeding Workflow
 * Handles all HTTP communication with the backend
 */

import type {
  SearchPlantsResponse,
  ValidatePlantResponse,
  StrainProgression,
  GetStrainResponse,
  CreateStrainResponse,
  SaveRunResponse,
  DestroyPlantResponse,
  SeedRoom,
  BreedingWorkflowResult,
} from '../types/breeding';

/**
 * Base API call helper with error handling
 */
async function apiCall<T>(endpoint: string, method = 'GET', body: unknown = null): Promise<T> {
  const options: {
    method: string;
    headers: { 'Content-Type': string };
    body?: string;
  } = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(endpoint, options);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data as T;
}

/**
 * Load available seed rooms from the server
 */
export async function loadSeedRooms(): Promise<SeedRoom[]> {
  const data = await apiCall<{ rooms: SeedRoom[] }>('/api/breeding/seed-rooms');
  return data.rooms;
}

/**
 * Search for plants by barcode (last 4 digits)
 */
export async function searchPlant(barcode: string): Promise<SearchPlantsResponse> {
  return apiCall<SearchPlantsResponse>(`/api/breeding/search-plant?barcode=${barcode}`);
}

/**
 * Validate that a plant can be used for breeding
 */
export async function validatePlant(plantId: number): Promise<ValidatePlantResponse> {
  return apiCall<ValidatePlantResponse>('/api/breeding/validate-plant', 'POST', { plantId });
}

/**
 * Generate the next strain name in the S-number progression
 */
export async function generateStrainName(currentName: string): Promise<StrainProgression> {
  return apiCall<StrainProgression>('/api/breeding/generate-strain-name', 'POST', {
    currentName,
  });
}

/**
 * Get an existing strain by name
 */
export async function getStrain(name: string): Promise<GetStrainResponse> {
  return apiCall<GetStrainResponse>(`/api/breeding/get-strain?name=${encodeURIComponent(name)}`);
}

/**
 * Create a new strain
 */
export async function createStrain(strainName: string): Promise<CreateStrainResponse> {
  return apiCall<CreateStrainResponse>('/api/breeding/create-strain', 'POST', { strainName });
}

/**
 * Toggle mother plant status
 */
export async function toggleMotherStatus(plantId: number): Promise<void> {
  await apiCall('/api/breeding/toggle-mother', 'POST', { plantId });
}

/**
 * Reassign plant to a new strain
 */
export async function reassignStrain(plantId: number, strainId: number): Promise<void> {
  await apiCall('/api/breeding/reassign-strain', 'POST', { plantId, strainId });
}

/**
 * Restore plant to original strain
 */
export async function restoreStrain(plantId: number, strainId: number): Promise<void> {
  await apiCall('/api/breeding/restore-strain', 'POST', { plantId, strainId });
}

/**
 * Create seed inventory from a mother plant
 */
export async function createSeeds(
  plantId: number,
  seedCount: number,
  roomId: number
): Promise<void> {
  await apiCall('/api/breeding/create-seeds', 'POST', { plantId, seedCount, roomId });
}

/**
 * Save breeding run data
 */
export async function saveBreedingRun(run: BreedingWorkflowResult): Promise<SaveRunResponse> {
  return apiCall<SaveRunResponse>('/api/breeding/save-run', 'POST', run);
}

/**
 * Destroy (retire) a mother plant
 */
export async function destroyPlant(
  plantId: number,
  reason: string,
  reasonType: number
): Promise<DestroyPlantResponse> {
  return apiCall<DestroyPlantResponse>('/api/breeding/plant/destroy', 'POST', {
    plantId,
    reason,
    reasonType,
  });
}
